// Types
type CustomerStatus = {
	Icon: string
	Description: string
}

type EmailWithDetails = {
	Label: string
	Value: string
	Status: CustomerStatus
}

type PhoneWithDetails = {
	Label: string
	Value: string
	Status: CustomerStatus
}

type DigitalCommunicationConsentOptionItem = {
	ID: string
	Display: string
	Selected: boolean
}

type DigitalCommunicationConsentOption = {
	Label: string
	ID: string
	Items: DigitalCommunicationConsentOptionItem[]
}

type SettlementOfferResponse = {
	DateFormat: string
	DefaultEndDate: string
	MinEndDate: string
	Accounts: string[]
	DefaultDiscountPercentage: number
	MinPercentage: number
	MaxPercentage: number
	FullBalance: string
}

type Account = {
	ID: string
	ClientID: string
	AccountRef: string
	AccountNumber: string
	ClientAccountRef: string
	CreatedDate: string
	Status: AccountStatus
	OriginalCreditor: string
	OriginalCreditorAccountNumber: string
	CurrentBalance: string
	AccruedInterest: string
	Fee: string
	AvailableForSIF: boolean
	LastPaymentDate: string
	LastPaymentAmount: string
}

type PaymentPlanAccount = {
	ID: string
	AccountRef: string
	Balance: string
	SOLExpired: boolean
	Details: AccountDetail[]
	ItemizationBalanceBreakdown: AccountDetail[]
	ReferredAt: string
	Closed: boolean
}

type ClientBanner = {
	Content: string
}

type CollectionHold = {
	HoldID: string
	Reason: string
	ReasonDisplay: string
	Display: string
	ReferenceID?: string
	ReferenceType?: string
	ManuallyReleasable: boolean
}

type DisputeReason = {
	ID: string
	Reason: string
}

type AccountDetail = {
	Label: string
	Value: string
	Warn: string
}

type PaymentPlanSettlementOffer = {
	DiscountRate: string
	AccountBalance: string
	DiscountAmount: string
	OfferEndDate: string
	SettlementLimitDate: string
}

type OutstandingAccount = {
	ID: string
	AccountRef: string
	Balance: string
	SOLExpired: boolean
	DisputeReasons: DisputeReason[]
	Details: AccountDetail[]
	ItemizationBalanceBreakdown: AccountDetail[]
	ReferredAt: string
	PaymentPlanSettlementOffers: PaymentPlanSettlementOffer[]
	Closed: boolean
}

type AccountStatus =
	| "PaymentBeingProcessed"
	| "InPaymentPlan"
	| "Settled"
	| "Disputed"
	| "Recalled"
	| "CustomerCaseEscalated"
	| "PendingInitialBankruptcyScrub"
	| "BankruptcyScrubDetected"
	| "PendingInitialServiceMembersScrub"
	| "ServiceMembersScrubDetected"
	| "PendingInitialDeceasedScrub"
	| "DeceasedScrubDetected"
	| "NotificationOfAssignmentPending"
	| "NotificationOfAssignmentPendingDueToEmailScrub"
	| "NotificationOfAssignmentPermanentlyUndelivered"
	| "NotificationOfAssignmentTemporarilyUndelivered"
	| "ClosedDueToStatuteOfLimitations"
	| "Outstanding"

type OtherAccount = {
	ID: string
	AccountRef: string
	Balance: string
	Status: AccountStatus
	SOLExpired: boolean
	Details: AccountDetail[]
	ItemizationBalanceBreakdown: AccountDetail[]
	ReferredAt: string
	Closed: boolean
}

type Transaction = {
	Amount: string
	Date: string
	Time: string
	Type: string
	Description: string
}

type SnoozeDuration = {
	ID: string
	Display: string
}

type EscalationOption = {
	ID: string
	Display: string
}

type CommsOptOutDate = {
	Label: string
	Display: string
}

type EmailLeadWithStatus = {
	Email: string
	IsEnabled: boolean
}

type CommsOptionItem = {
	ID: string
	Display: string
}

type CommsOption = {
	Label: string
	ID: string
	Items: CommsOptionItem[]
}

type ContactInstruction = {
	Content: string
}

type EmailLeadRemovalReason = {
	ID: string
	Display: string
}

type Note = {
	[key: string]: any
}

// Case Management Types
type CaseType = {
	ID: string
	Label: string
}

type CaseResolutionReason = {
	ID: string
	Label: string
}

type CaseResolution = {
	ID: string
	Label: string
	Description?: string
	ConsequenceDescription?: string
	ConsequenceAlert?: boolean
	CallToAction?: string
	Reasons: CaseResolutionReason[]
	NoteForms: CaseNoteForm[]
}

type CaseNoteStatus = {
	ID: string
	Label: string
}

type CaseNoteArchiveStatus = {
	ID: string
	Label: string
}

type CaseNote = {
	ID: string
	Label: string
	CustomerID: string
	Form: {
		Type: string
		Version: number
		Fields: Array<{Name: string; Type: string; Content: string}>
	}
	Uploads: Array<{ID: string; FileName: string}>
	AgentName: string
	AgentEmail: string
	Status: CaseNoteStatus
	ArchiveStatuses: CaseNoteArchiveStatus[]
	CreatedAt: string
}

type CaseNoteForm = {
	Type: string
	Label: string
	Version: number
	Fields: Array<{Name: string; Type: string; Required: boolean}>
}

type ActiveCase = {
	CaseID: string
	CaseType: string
	Resolutions: CaseResolution[]
	ExpiryDate: string
	SubmitterType: string
	SubmitterName: string
	SubmitterEmail: string
	SubmittedAt: string
	Notes: CaseNote[]
	AttachNoteForms: CaseNoteForm[]
}

type ClosedCase = {
	CaseID: string
	CaseType: string
	Resolution: string | null
	Reason: string | null
	ExpiryDate: string
	SubmitterType: string
	SubmitterName: string
	SubmitterEmail: string
	ResolverType: string
	ResolverName: string
	ResolverEmail: string
	SubmittedAt: string
	ClosedAt: string
	Notes: CaseNote[]
}

type CasesResponse = {
	CaseTypes: CaseType[]
	ActiveCases: ActiveCase[]
	ClosedCases: ClosedCase[]
}

type ExpiryDuration = {
	ID: string
	Display: string
}

type ScaffoldCaseResponse = {
	CaseID: string
	CaseType: string
	CaseTypeLabel: string
	NoteForms: Array<{
		Type: string
		Label?: string
		Version: number
		Fields: Array<{Name: string; Type: string; Required: boolean}>
	}>
	ExpiryConfig: {
		DefaultExpiryDuration: ExpiryDuration
		AllowSpecificExpiryDate: boolean
		AllowedDurations: ExpiryDuration[]
	} | null
	ActiveCaseIDs: string[]
}

type LatitudeInfo = {
	Link?: string
	CollectionCommsEnabled?: boolean
	PaymentsEnabled?: boolean
}

type ActivePaymentPlan = {
	ID: string
	Frequency: string
	Amount: string
	InstalmentAmount: string
	StartDate: string
	EndDate: string
	PaymentMethod: string
	NextUpcomingInstalmentDate: string
	Offers: any[]
	ManagePlanURL: string
}

type CustomerCreatedActivityPayload = {
	Name: string
	DOB: string
	Emails: string[]
	Phones: string[]
	Address: string
}

type CustomerLatitudeReferredActivityPayload = {
	Name: string
	LatitudeID: string
	CustomerRef: string
	DOB: string
	Emails: string[]
	Phones: string[]
	Address: string
	IdentificationDocument: string
}

type CustomerAddressChangedActivityPayload = {
	NewAddress: string
}

type CustomerAddressChangedByCustomerActivityPayload = {
	NewAddress: string
	AgentEmail: string
}

type CustomerCaseEscalatedActivityPayload = Record<string, never>

type CustomerCaseSubmittedActivityPayload = {
	CaseID: string
	CaseType: string
	SubmitterType: string
	SubmitterEmail: string
	SubmitterName: string
}

type CustomerCaseResolvedActivityPayload = {
	CaseID: string
	CaseType: string
	Resolution: string
	ResolverType: string
	ResolverEmail: string
	ResolverName: string
}

type CustomerCommunicationSnoozedActivityPayload = {
	AgentEmail: string
	EndDate: string
}

type CustomerDigitalCommunicationConsentActivityPayload = Record<string, never>

type CustomerEmailCommunicationOptedActivityPayload = Record<string, never>

type CustomerEmailLeadRemovedActivityPayload = {
	EmailLead: string
	AgentEmail: string
	Reason: string
}

type CustomerEmailLeadsUpdateActivityPayload = {
	Emails: string[]
}

type CustomerEmailSentActivityPayload = {
	ContentURI: string
	Summary: string
	Recipient: string
}

type CustomerEmailUpdateActivityPayload = {
	Email: string
}

type CustomerEmailUpdateByCustomerActivityPayload = {
	Email: string
	AgentEmail: string
}

type CustomerESIGNConsentRequestedPayload = {
	AgentEmail: string
	To: string
	Subject: string
}

type CustomerESIGNConsentRevokedByAgentPayload = {
	RevokedAt: string
	RevokedVia: string
	AgentEmail: string
}

type CustomerInformationUpdatedActivityPayload = {
	Name: string
	DOB: string
}

type CustomerInformationUpdatedByCustomerActivityPayload = {
	Name: string
	DOB: string
	AgentEmail: string
}

type CustomerInstalmentOverrideAppliedActivityPayload = {
	WeeklyMaxDurationOverride: number
	PaymentAmount: number
	AppliedAtBalance: number
	Currency: string
	AgentEmail: string
}

type CustomerInstalmentOverrideResetActivityPayload = {
	WeeklyMaxDuration: number
	PaymentAmount: number
	ResetAtBalance: number
	Currency: string
	AgentEmail: string
}

type CustomerNOALetterSentActivityPayload = {
	ContentURI: string
	ContentDownloadURI: string
}

type CustomerPaymentArrangementCancelled = {
	PaymentArrangementID: string
	Reason: string
}

type CustomerPaymentArrangementCompleted = {
	PaymentArrangementID: string
}

type CustomerPaymentArrangementSetupSucceeded = {
	PaymentArrangementID: string
	StartDate: string
	EndDate: string
	InstalmentAmount: string
	NumberOfInstalments: number
	Frequency: string
}

type CustomerPaymentPlanCancelled = {
	PaymentPlanID: string
	Reason: string
}

type CustomerPaymentPlanCompleted = {
	PaymentPlanID: string
}

type CustomerPaymentPlanSetupSucceeded = {
	PaymentPlanID: string
	Amount: string
	StartDate: string
	EndDate: string
	InstalmentAmount: string
	NumberOfInstalments: number
	Frequency: string
}

type CustomerPhoneIdentityMatchActivityPayload = {
	Phone: string
}

type CustomerPhoneReassignedNumberActivityPayload = {
	Phone: string
}

type CustomerPhoneOptedOutForSMSActivityPayload = {
	Phone: string
}

type CustomerPhoneUpdateActivityPayload = {
	Phone: string
}

type CustomerPhoneUpdateByCustomerActivityPayload = {
	Phone: string
	AgentEmail: string
}

type CustomerPreferredLanguageConfiguredActivityPayload = Record<string, never>

type CustomerSMSActivityPayload = {
	SMSID: string
	ToNumber: string
	Text: string
	DeliveryAt: string
	SentAt: string
	FailedAt: string
	Type: string
	InitiatedAt: string
	FailedReason: string
}

type CustomerActivityBase = {
	Title: string
	HappenedAt: string
}

type CustomerActivityTypePayload =
	| {Type: "CustomerCreated"; Payload: CustomerCreatedActivityPayload}
	| {Type: "CustomerLatitudeReferred"; Payload: CustomerLatitudeReferredActivityPayload}
	| {
			Type: "CustomerAddressChanged"
			Payload: CustomerAddressChangedActivityPayload | CustomerAddressChangedByCustomerActivityPayload
	  }
	| {Type: "CustomerCaseEscalated"; Payload: CustomerCaseEscalatedActivityPayload}
	| {Type: "CustomerCaseSubmitted"; Payload: CustomerCaseSubmittedActivityPayload}
	| {Type: "CustomerCaseResolved"; Payload: CustomerCaseResolvedActivityPayload}
	| {Type: "CustomerCommunicationSnoozed"; Payload: CustomerCommunicationSnoozedActivityPayload}
	| {Type: "CustomerDigitalCommunicationConsentGranted"; Payload: CustomerDigitalCommunicationConsentActivityPayload}
	| {Type: "CustomerDigitalCommunicationConsentDenied"; Payload: CustomerDigitalCommunicationConsentActivityPayload}
	| {
			Type: "CustomerEmailAdded"
			Payload: CustomerEmailUpdateActivityPayload | CustomerEmailUpdateByCustomerActivityPayload
	  }
	| {Type: "CustomerEmailLeadsAddedByDataWashing"; Payload: CustomerEmailLeadsUpdateActivityPayload}
	| {
			Type: "CustomerEmailRemoved"
			Payload: CustomerEmailUpdateActivityPayload | CustomerEmailUpdateByCustomerActivityPayload
	  }
	| {Type: "CustomerEmailLeadRemovedByCustomer"; Payload: CustomerEmailLeadRemovedActivityPayload}
	| {Type: "CustomerEmailLeadRemovedByThirdParty"; Payload: CustomerEmailLeadRemovedActivityPayload}
	| {
			Type: "CustomerPhoneAdded"
			Payload: CustomerPhoneUpdateActivityPayload | CustomerPhoneUpdateByCustomerActivityPayload
	  }
	| {
			Type: "CustomerPhoneRemoved"
			Payload: CustomerPhoneUpdateActivityPayload | CustomerPhoneUpdateByCustomerActivityPayload
	  }
	| {Type: "CustomerPhoneIdentityMatchedByScrub"; Payload: CustomerPhoneIdentityMatchActivityPayload}
	| {Type: "CustomerPhoneIdentityNotMatchedByScrub"; Payload: CustomerPhoneIdentityMatchActivityPayload}
	| {Type: "CustomerEmailCommunicationsOptedIn"; Payload: CustomerEmailCommunicationOptedActivityPayload}
	| {Type: "CustomerEmailCommunicationsOptedOut"; Payload: CustomerEmailCommunicationOptedActivityPayload}
	| {
			Type: "CustomerInformationUpdated"
			Payload: CustomerInformationUpdatedActivityPayload | CustomerInformationUpdatedByCustomerActivityPayload
	  }
	| {Type: "CustomerPreferredLanguageConfigured"; Payload: CustomerPreferredLanguageConfiguredActivityPayload}
	| {Type: "ESIGNConsentGranted"; Payload: CustomerDigitalCommunicationConsentActivityPayload}
	| {Type: "ESIGNConsentRequested"; Payload: CustomerESIGNConsentRequestedPayload}
	| {Type: "ESIGNConsentRevoked"; Payload: CustomerESIGNConsentRevokedByAgentPayload}
	| {Type: "CustomerPhoneHasBeenReassigned"; Payload: CustomerPhoneReassignedNumberActivityPayload}
	| {Type: "CustomerPhoneHasNotBeenReassigned"; Payload: CustomerPhoneReassignedNumberActivityPayload}
	| {Type: "CustomerPhoneOptedOutForSMS"; Payload: CustomerPhoneOptedOutForSMSActivityPayload}
	| {Type: "CustomerInstalmentOverrideApplied"; Payload: CustomerInstalmentOverrideAppliedActivityPayload}
	| {Type: "CustomerInstalmentOverrideReset"; Payload: CustomerInstalmentOverrideResetActivityPayload}
	| {Type: "SMSSent"; Payload: CustomerSMSActivityPayload}
	| {Type: "SMSFailed"; Payload: CustomerSMSActivityPayload}
	| {Type: "WelcomeSMSSent"; Payload: CustomerSMSActivityPayload}
	| {Type: "WelcomeSMSFailed"; Payload: CustomerSMSActivityPayload}
	| {Type: "HelpSMSSent"; Payload: CustomerSMSActivityPayload}
	| {Type: "HelpSMSFailed"; Payload: CustomerSMSActivityPayload}
	| {Type: "EmailSent"; Payload: CustomerEmailSentActivityPayload}
	| {Type: "NOASent"; Payload: CustomerEmailSentActivityPayload}
	| {Type: "NOALetterSent"; Payload: CustomerNOALetterSentActivityPayload}
	| {Type: "PaymentPlanSetupSucceeded"; Payload: CustomerPaymentPlanSetupSucceeded}
	| {Type: "PaymentPlanCompleted"; Payload: CustomerPaymentPlanCompleted}
	| {Type: "PaymentPlanCancelled"; Payload: CustomerPaymentPlanCancelled}
	| {Type: "PaymentArrangementSetupSucceeded"; Payload: CustomerPaymentArrangementSetupSucceeded}
	| {Type: "PaymentArrangementCompleted"; Payload: CustomerPaymentArrangementCompleted}
	| {Type: "PaymentArrangementCancelled"; Payload: CustomerPaymentArrangementCancelled}

type CustomerActivity = CustomerActivityBase & CustomerActivityTypePayload

type AccountActivityPayload = {
	ContentURI?: string
	Summary?: string
	Recipient?: string
	PaymentArrangementID?: string
	Reason?: string
	StartDate?: string
	EndDate?: string
	InstalmentAmount?: string
	NumberOfInstalments?: number
	Frequency?: string
	Name?: string
	DOB?: string
	Emails?: string[]
	Phones?: string[]
	Address?: string
	[key: string]: any
}

type AccountActivityField = {
	Label: string
	Value: string
}

type AccountActivity = {
	Fields: AccountActivityField[]
	HappenedAt: string
	IconType: string
	Title: string
	Type: string
}

type CustomerActivitiesResponse = {
	Activities: CustomerActivity[]
}

type AccountActivitiesResponse = {
	Activities: AccountActivity[]
}

type ActiveInstalmentCap = {
	MaxPaymentsWeekly: number
	MinPaymentAmount: number
}

type InstalmentCapHistoryEntry = {
	Type: string
	MaxPaymentsWeekly: number
	MinPaymentAmount: number
	Balance: number
	PerformedAt: string
	AgentEmail: string
}

type InstalmentCapsResponse = {
	ActiveInstalmentCap: ActiveInstalmentCap
	DefaultInstalmentCap: ActiveInstalmentCap
	InstalmentCaps: InstalmentCapHistoryEntry[]
	MaximumExtendableDuration: number
}

type Customer = {
	ID: string
	ClientID: string
	ClientName: string
	ClientDisplayName: string
	CustomerRef: string
	DOB: string
	FullName: string
	Address: string
	TotalCustomerBalanceInCents: number
	TotalCustomerAvailableBalanceInCents: number
	IdentificationDocument: string
	CountryForAddressUpdate: string
	CommunicationSnoozed: boolean
	SnoozedUntil: string
	OutstandingAccounts: OutstandingAccount[]
	PaymentPlanAccounts: PaymentPlanAccount[]
	Transactions: Transaction[]
	OtherAccounts: OtherAccount[]
	TotalOutstanding: string
	TotalInPaymentPlan: string
	TotalOther: string
	Emails: string[]
	EmailLeads: string[] | null
	EmailLeadsWithStatus: EmailLeadWithStatus[]
	EmailLeadRemovalReasons: EmailLeadRemovalReason[]
	PreferredLanguage: string
	SnoozeDurations: SnoozeDuration[]
	Escalations: EscalationOption[]
	Escalation: string
	ActivePaymentPlans: ActivePaymentPlan[]
	CommsOptOutDates: CommsOptOutDate[]
	CommsOptions: CommsOption[]
	LocalTime: string
	Notes: Note[]
	DigitalCommunicationConsentOptions: DigitalCommunicationConsentOption[]
	AllowGeneratePaymentLink: boolean
	AllAccountsBalanceBreakdown: AccountDetail[]
	WrittenStatuteOfLimitationsDisclosures: string
	VerbalStatuteOfLimitationsDisclosures: string
	NextCompliantTimeRangeInViewerLocalTime: string
	ContactFrequencyRules: string[]
	ContactInstructions: ContactInstruction[]
	EmailsWithDetails: EmailWithDetails[]
	PhonesWithDetails: PhoneWithDetails[]
	EnableCreateSettlementOffer: boolean
	ESIGNConsentRequired: boolean
	ESIGNConsentRequested: boolean
	ESIGNConsentRequestedAt: string | null
	ESIGNConsentGrantedAt: string | null
	CustomerPaidCommissions: boolean
	SystemOfRecord: string
	LatitudeLink: string
	Latitude: LatitudeInfo | null
	ClientBanners: ClientBanner[]
	EnableRocketOffersTab: boolean
	EnableCustomerOffersV2: boolean
	UseNotesV2: boolean
	EnableRocketCasesTab: boolean
	EnableRocketCollectionHolds: boolean
	CollectionHolds: CollectionHold[] | null
}

type CustomerSearchResult = {
	ID: string
	FullName: string
	DOB: string
	Emails: string[]
	ClientName: string
	SystemOfRecord: string
	CollectionHold: boolean
	OtherAccountIDs: Record<string, string>
	OutstandingAccountIDs: Record<string, string>
}

type CustomerSearchResponse = CustomerSearchResult[]

type OfferDetails = {
	ExpireDate: string
	Balance: string
	DiscountPercentage: string
	DiscountedAmount: string
	AmountToPay: string
	SettlementLimitDate?: string
}

type OfferData = {
	InFull?: OfferDetails
	PaymentPlan?: OfferDetails
}

type CustomerOfferTier = {
	TierID: string
	TierType: string
	MinimumNumberOfPayments?: number
	MaximumNumberOfPayments: number
	DiscountRate: number
	MinimumPaymentAmount: number
	Currency: string
	TimeFrameWeeks: number
}

type CustomerOffer = {
	OfferID: string
	StartDate: string
	EndDate: string
	Tiers: CustomerOfferTier[]
	CampaignID?: string
	AgentEmail?: string
	Notes?: string
	CreatedAt: string
	Status: "ACTIVE" | "REVOKED" | "EXPIRED"
}

type RevocationReason = {
	Value: string
	Label: string
}

type CustomerOffersResponse = {
	ActiveOffers: CustomerOffer[]
	InactiveOffers: CustomerOffer[]
	RevocationReasons: RevocationReason[]
}

type DevToolsStatementOfWork = {
	id: string
	productType: string
	payoutType: string
}

type DevToolsClient = {
	id: string
	name: string
	country: string
	statementsOfWork: DevToolsStatementOfWork[]
}

type DevToolsAccountInput = {
	balance: number
	statementOfWorkID: string
}

type DevToolsCustomerInput = {
	fullName: string
	dob: string
	email: string
	phone: string
}

type DevToolsCreateCustomerRequest = {
	clientID: string
	customer: DevToolsCustomerInput
	accounts: DevToolsAccountInput[]
	expediteProcesses: boolean
	simulateScrubs: boolean
}

type DevToolsCreateCustomerResponse = {
	customerID: string
	secureCode: string
	paymentPortalURL: string
}

class API {
	customer: CustomerAPI
	account: AccountAPI
	paymentPlan: PaymentPlan
	devTools: DevToolsAPI
	url: (path: string) => string

	constructor() {
		this.customer = new CustomerAPI()
		this.account = new AccountAPI()
		this.paymentPlan = new PaymentPlan()
		this.devTools = new DevToolsAPI()
		this.url = (path) => apiBaseUrl() + path
	}
}

function RelativeURL(path: string) {
	const defaultBase = "https://path"
	const url = new URL(path, defaultBase)

	function appendQueryParam(name: string, value: string) {
		const newURL = new URL(url)
		newURL.searchParams.append(name, value)

		return RelativeURL(newURL.toString().replace(defaultBase, ""))
	}

	function toString() {
		return url.toString().replace(defaultBase, "")
	}

	return {appendQueryParam, toString}
}

class CustomerAPI {
	search(query: string): Promise<CustomerSearchResponse> {
		return get(`/customers?q=${encodeURIComponent(query)}`)
	}

	get(id: string, viewerTimezoneOffset: number | string): Promise<Customer> {
		return get(`/customers/${id}?ViewerTimezoneOffsetInMinutes=${viewerTimezoneOffset}`)
	}

	snooze(
		id: string,
		payload: {
			Duration: string
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/snooze`, payload)
	}

	releaseCollectionHold(
		id: string,
		holdID: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/collection-holds/${holdID}/release`, payload)
	}

	escalateCase(
		id: string,
		payload: {
			Escalation: string
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/escalate-case`, payload)
	}

	addEmail(
		id: string,
		payload: {
			Email: string | undefined
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/add-email`, payload)
	}

	changeAddress(
		id: string,
		payload: {
			Line1: string | undefined
			Line2: string | undefined
			City: string | undefined
			State: string | undefined
			Postcode: string | undefined
			Country: string | undefined
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/change-address`, payload)
	}

	removeEmail(
		id: string,
		payload: {
			Email: string
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/remove-email`, payload)
	}

	removeEmailLead(
		id: string,
		payload: {
			EmailLead: string
			AgentEmail: string | undefined
			Reason: string
		},
	) {
		return post(`/customers/${id}/remove-email-lead`, payload)
	}

	addPhone(
		id: string,
		payload: {
			Phone: string | undefined
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/add-phone`, payload)
	}

	removePhone(
		id: string,
		payload: {
			Phone: string
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/remove-phone`, payload)
	}

	changeComms(
		id: string,
		{actionID, channel, ...payload}: {actionID: string; channel: string; AgentEmail: string | undefined},
	) {
		return post(`/customers/${id}/${actionID}/${channel}`, payload)
	}

	changeDigitalCommConsent(id: string, {actionID, ...payload}: {actionID: string; AgentEmail: string | undefined}) {
		return post(`/customers/${id}/digital-communication-consent/${actionID}`, payload)
	}

	addNote(
		customerID: string,
		payload: {
			Body: string
			AgentEmail: string | undefined
			AgentName: string | undefined
		},
	) {
		return post(`/customers/${customerID}/add-note`, payload)
	}

	generatePaymentLink(customerID: string) {
		return post(`/customers/${customerID}/payment-link`)
	}

	activities(customerID: string): Promise<CustomerActivitiesResponse> {
		return get(`/customers/${customerID}/activities`)
	}

	downloadEmailPDFURL(customerID: string, activityID: string) {
		return apiBaseUrl() + `/customers/${customerID}/activities/email/${activityID}/pdf`
	}

	settlementOfferCreate(
		customerID: string,
		payload: {
			CampaignEndDate: string
			Discount: string | undefined
			AccountIDs: string[]
		},
	) {
		return post(`/customers/${customerID}/settlement-offer`, payload)
	}

	settlementOfferCalculate(
		customerID: string,
		payload: {
			CampaignEndDate: string
			Discount: string | undefined
			AccountIDs: string[]
		},
	) {
		return post(`/customers/${customerID}/settlement-offer/calculate`, payload)
	}

	getSettlementOffer(customerID: string): Promise<SettlementOfferResponse> {
		return get(`/customers/${customerID}/settlement-offer`)
	}

	getEligibleOffers(customerID: string): Promise<OfferData> {
		return get(`/customers/${customerID}/eligible-offers`)
	}

	createOffer(
		customerID: string,
		payload: {
			offerId: string
			startDate: string
			endDate: string
			tiers: Array<
				| {tierId: string; tierType: "ONE_OFF"; discountRate: number}
				| {tierId: string; tierType: "PLAN"; discountRate: number; timeFrameWeeks: number}
			>
			agentEmail?: string
			notes?: string
		},
	) {
		return post(`/customers/${customerID}/offers`, payload)
	}

	getOffers(customerID: string): Promise<CustomerOffersResponse> {
		return get(`/customers/${customerID}/offers`)
	}

	revokeOffer(
		customerID: string,
		offerID: string,
		payload: {
			Reason: string
			AgentEmail: string | undefined
			Notes?: string
		},
	) {
		return post(`/customers/${customerID}/offers/${offerID}/revoke`, payload)
	}

	requestESIGNConsent(customerID: string, agentEmail: string | undefined) {
		return post(`/customers/${customerID}/request-esign-consent`, {
			AgentEmail: agentEmail,
		})
	}

	revokeESIGNConsent(customerID: string, revokedAt: string, revokedVia: string, agentEmail: string) {
		return post(`/customers/${customerID}/revoke-esign-consent`, {
			RevokedAt: revokedAt,
			RevokedVia: revokedVia,
			AgentEmail: agentEmail,
		})
	}

	getInstalmentCaps(customerID: string): Promise<InstalmentCapsResponse> {
		return get(`/customers/${customerID}/instalmentcaps`)
	}

	extendInstalmentCap(customerID: string, maxPayments: number, agentEmail: string | undefined): Promise<void> {
		return post(`/customers/${customerID}/instalmentcaps`, {
			WeeklyMaxDurationOverride: maxPayments,
			AgentEmail: agentEmail,
		})
	}

	resetInstalmentCap(customerID: string, agentEmail: string | undefined): Promise<void> {
		return del(`/customers/${customerID}/instalmentcaps`, {
			AgentEmail: agentEmail,
		})
	}

	getNotes(customerID: string): Promise<{
		Forms: Array<{
			Type: string
			Version: number
			Fields: Array<{Name: string; Type: string; Required: boolean}>
		}>
		Notes: Array<{
			ID: string
			CustomerID: string
			Form: {
				Type: string
				Version: number
				Fields: Array<{Name: string; Type: string; Content: string | number}>
			}
			Uploads: Array<{ID: string; FileName: string}>
			AgentName: string
			AgentEmail: string
			CreatedAt: string
		}>
		AttachmentsInNotesEnabled: boolean
	}> {
		return get(`/customers/${customerID}/notes`)
	}

	createNote(
		customerID: string,
		payload: {
			FormType: string
			FormVersion: number
			Answers: Array<{Name: string; Type: string; Content: string | number}>
			Uploads: Array<{ID: string; FileName: string}>
			AgentName: string | undefined
			AgentEmail: string | undefined
			Pending?: boolean
		},
	): Promise<{NoteID: string}> {
		return post(`/customers/${customerID}/notes`, payload)
	}

	getUploadURL(customerID: string): Promise<{UploadID: string; SignedURL: string}> {
		return post(`/customers/${customerID}/uploads`)
	}

	getUploadContentURL(customerID: string, uploadID: string): string {
		return apiBaseUrl() + `/customers/${customerID}/uploads/${uploadID}/content`
	}

	getNoteUploadContentURL(customerID: string, noteID: string, uploadID: string): string {
		return apiBaseUrl() + `/customers/${customerID}/notes/${noteID}/uploads/${uploadID}/content`
	}

	// Case Management
	getCases(customerID: string): Promise<CasesResponse> {
		return get(`/customers/${customerID}/cases`)
	}

	scaffoldCase(customerID: string, caseType: string): Promise<ScaffoldCaseResponse> {
		return post(`/customers/${customerID}/cases?caseType=${encodeURIComponent(caseType)}`)
	}

	submitCase(
		customerID: string,
		caseID: string,
		payload: {
			CaseType: string
			NoteID: string
			ExpiryDuration?: string
			AgentName: string | undefined
			AgentEmail: string | undefined
		},
	): Promise<void> {
		return put(`/customers/${customerID}/cases/${caseID}`, payload)
	}

	resolveCase(
		customerID: string,
		caseID: string,
		payload: {
			Resolution: string
			Reason?: string
			NoteID?: string
			AgentName: string | undefined
			AgentEmail: string | undefined
		},
	): Promise<void> {
		return post(`/customers/${customerID}/cases/${caseID}/resolve`, payload)
	}

	getCaseNotes(customerID: string, caseID: string): Promise<CaseNote[]> {
		return get(`/customers/${customerID}/cases/${caseID}/notes`)
	}

	attachCaseNote(customerID: string, caseID: string, noteID: string): Promise<void> {
		return post(`/customers/${customerID}/cases/${caseID}/notes`, {NoteID: noteID})
	}

	archiveNote(
		customerID: string,
		noteID: string,
		payload: {
			Status: string
		},
	): Promise<void> {
		return post(`/customers/${customerID}/notes/${noteID}/archive`, payload)
	}
}

class AccountAPI {
	dispute(
		id: string,
		dispute: {
			Reason: string
			AgentEmail: string | undefined
		},
	) {
		return post(`/accounts/${id}/dispute`, dispute)
	}

	acknowledgeNotificationOfAssignment(
		id: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/accounts/${id}/acknowledge-notification-of-assignment`, payload)
	}

	reTriggerNotificationOfAssignment(
		id: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/accounts/${id}/re-trigger-notification-of-assignment`, payload)
	}

	recall(
		id: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/accounts/${id}/recall`, payload)
	}

	isReopenAfterRecalledEnabled() {
		return get("/accounts/reopen-after-recalled/enabled")
	}

	reopenAfterRecalled(
		id: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/accounts/${id}/reopen-after-recalled`, payload)
	}

	activities(id: string): Promise<AccountActivitiesResponse> {
		return get(`/accounts/${id}/activities`)
	}
}

class PaymentPlan {
	cancel(
		id: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/payment-plans/${id}/cancel`, payload)
	}
}

class DevToolsAPI {
	listClients(): Promise<DevToolsClient[]> {
		return get("/dev/clients")
	}

	createCustomer(payload: DevToolsCreateCustomerRequest): Promise<DevToolsCreateCustomerResponse> {
		return post("/dev/customers", payload)
	}
}

function get(endpoint: string, options = {}) {
	return req(
		RelativeURL(endpoint)
			.appendQueryParam("AgentEmail", window.localStorage.getItem("agentEmail") || "")
			.toString(),
		{...options, method: "GET"},
	)
}

function post(endpoint: string, body: any = undefined, options = {}) {
	return req(endpoint, {...options, method: "POST", body: JSON.stringify(body)})
}

function put(endpoint: string, body: any = undefined, options = {}) {
	return req(endpoint, {...options, method: "PUT", body: JSON.stringify(body)})
}

function del(endpoint: string, body: any = undefined, options = {}) {
	return req(endpoint, {...options, method: "DELETE", body: JSON.stringify(body)})
}

function req(endpoint: string, options: RequestInit) {
	return new Promise<any>((resolve, reject) => {
		fetch(apiBaseUrl() + endpoint, options)
			.then((res) => {
				const contentType = res.headers.get("Content-Type")
				const isJSON = contentType && contentType.includes("application/json")
				const promise = isJSON ? res.json() : res.text()
				promise.then((body) => (res.status >= 400 ? reject(apiError(body)) : resolve(body)))
			})
			.catch(() => {
				reject(apiError("Network error"))
			})
	})
}

function apiBaseUrl() {
	return import.meta.env.VITE_API_URL
}

function apiError(error: unknown) {
	const unknownError = {Message: "Unknown error"}
	if (!error) return unknownError
	if (typeof error == "string") return {Message: error}
	if (typeof error === "object") {
		if ("message" in error && typeof error.message === "string") {
			return {Message: error.message}
		}
		if ("Message" in error) return error as {Message: string}
		if ("Errors" in error && typeof error.Errors === "object" && error.Errors !== null) {
			const message = Object.keys(error.Errors)
				.map((key) => {
					const errorValue = (error.Errors as Record<string, unknown>)[key]
					if (Array.isArray(errorValue)) {
						return errorValue.map((msg) => `${key} ${msg}`)
					}
					return []
				})
				.flat()
				.join(".")
			return {Message: message}
		}
	}
	return unknownError
}

const api = new API()

export {api}
export type {
	Customer,
	CustomerSearchResponse,
	CustomerSearchResult,
	CustomerStatus,
	EmailWithDetails,
	PhoneWithDetails,
	Account,
	PaymentPlanAccount,
	ClientBanner,
	ActivePaymentPlan,
	ActiveInstalmentCap,
	InstalmentCapHistoryEntry,
	InstalmentCapsResponse,
	OutstandingAccount,
	CustomerActivity,
	CustomerActivitiesResponse,
	AccountActivity,
	AccountActivityPayload,
	AccountActivitiesResponse,
	OtherAccount,
	AccountStatus,
	CustomerCreatedActivityPayload,
	CustomerLatitudeReferredActivityPayload,
	CustomerAddressChangedActivityPayload,
	CustomerAddressChangedByCustomerActivityPayload,
	CustomerCaseEscalatedActivityPayload,
	CustomerCaseSubmittedActivityPayload,
	CustomerCaseResolvedActivityPayload,
	CustomerCommunicationSnoozedActivityPayload,
	CustomerDigitalCommunicationConsentActivityPayload,
	CustomerEmailCommunicationOptedActivityPayload,
	CustomerEmailLeadRemovedActivityPayload,
	CustomerEmailLeadsUpdateActivityPayload,
	CustomerEmailSentActivityPayload,
	CustomerEmailUpdateActivityPayload,
	CustomerEmailUpdateByCustomerActivityPayload,
	CustomerESIGNConsentRequestedPayload,
	CustomerESIGNConsentRevokedByAgentPayload,
	CustomerInformationUpdatedActivityPayload,
	CustomerInformationUpdatedByCustomerActivityPayload,
	CustomerInstalmentOverrideAppliedActivityPayload,
	CustomerInstalmentOverrideResetActivityPayload,
	CustomerNOALetterSentActivityPayload,
	CustomerPaymentArrangementCancelled,
	CustomerPaymentArrangementCompleted,
	CustomerPaymentArrangementSetupSucceeded,
	CustomerPaymentPlanCancelled,
	CustomerPaymentPlanCompleted,
	CustomerPaymentPlanSetupSucceeded,
	CustomerPhoneIdentityMatchActivityPayload,
	CustomerPhoneOptedOutForSMSActivityPayload,
	CustomerPhoneReassignedNumberActivityPayload,
	CustomerPhoneUpdateActivityPayload,
	CustomerPhoneUpdateByCustomerActivityPayload,
	CustomerPreferredLanguageConfiguredActivityPayload,
	CustomerSMSActivityPayload,
	ContactInstruction,
	Note,
	SettlementOfferResponse,
	Transaction,
	OfferData,
	OfferDetails,
	CustomerOffer,
	CustomerOfferTier,
	CustomerOffersResponse,
	RevocationReason,
	CaseType,
	CaseResolution,
	CaseResolutionReason,
	CaseNote,
	CaseNoteStatus,
	CaseNoteArchiveStatus,
	CaseNoteForm,
	ActiveCase,
	ClosedCase,
	CasesResponse,
	ExpiryDuration,
	ScaffoldCaseResponse,
	CollectionHold,
	DevToolsStatementOfWork,
	DevToolsClient,
	DevToolsCreateCustomerRequest,
	DevToolsCreateCustomerResponse,
}
